import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAppState } from "../../contexts/App";
import { useSite } from "../../contexts/Site";
import { getPages, siteHasAgendaPage } from "../../model/pages";
import { siteUsesPlusStyle } from "../../model/plusFeatures";
import { availableSections } from "../../model/sectionDefinitions";
import { withPage } from "../CurrentPage";

export type PlusBenefit = {
  key: string;
  title: string;
  description: string;
  /** Where the benefit is set up: an app route, or an external link when `external`. */
  to: string;
  external?: boolean;
};

export type TrackedPlusBenefit = PlusBenefit & { inUse: boolean };

/**
 * The PLUS benefits of the Keepo being edited. `tracked` count in "you use X of Y": the ones a PLUS
 * Keepo always has (unlimited sections, analytics, chat support, so the count never starts at 0) and
 * the ones we can tell whether it uses (brand, PLUS sections, tracking integrations). `included` are
 * part of the plan but have no "in use" state we can read (custom domain).
 */
export const usePlusBenefits = () => {
  const { t } = useTranslation();
  const { site } = useSite();
  const { appState } = useAppState();

  return useMemo(() => {
    const sectionsPrefix = "pages.editor.sections.actions.add.modal.descriptions";
    const trackingPrefix = "pages.settings.components.advancedConfigs.tracking";
    const tracking = appState.editingSiteTrackingIntegrations || {};

    // the home's sections and each page's, with the page they're on (null: the home)
    const everySection = [
      ...(site?.sections || []).map(section => ({ section, pageId: null as string | null })),
      ...getPages(site).flatMap(page => page.sections.map(section => ({ section, pageId: page.id as string | null }))),
    ];

    const sections: TrackedPlusBenefit[] = availableSections
      .filter(section => section.pro)
      .map(({ type, subType, multiple }) => {
        const key = subType ? `${type}-${subType}` : type;
        const matching = everySection.filter(({ section }) => section.type === type && section.subType === subType);
        const active = matching.find(({ section }) => section.active);
        // Opens the section's own screen (on its page): the one in use to edit it, a new one otherwise. A
        // section the Keepo can have only once (form, floating WhatsApp) reopens the existing one even when hidden.
        const existing = active || (!multiple ? matching[0] : undefined);
        const path = `/editor/${type}${subType ? `/${subType}` : ""}`;
        return {
          key,
          title: t(subType ? `site.sections.${type}.${subType}` : `site.sections.${type}`),
          description: t(`${sectionsPrefix}.${key}`),
          to: existing ? withPage(`${path}?id=${existing.section.id}`, existing.pageId) : path,
          inUse: !!active,
        };
      });

    const trackers: TrackedPlusBenefit[] = [
      { key: "facebookPixel", path: "facebook-pixel", inUse: !!tracking.facebookPixel },
      { key: "googleAnalytics", path: "google-analytics", inUse: !!tracking.googleAnalytics },
      { key: "tagManager", path: "tag-manager", inUse: !!tracking.tagManager },
    ].map(({ key, path, inUse }) => ({
      key,
      title: t(`${trackingPrefix}.${key}.title`),
      description: t(`${trackingPrefix}.${key}.description`),
      to: `/settings/${path}`,
      inUse,
    }));

    // Always in use on a PLUS Keepo
    const always: TrackedPlusBenefit[] = [
      {
        key: "sections",
        title: t("pages.plusBenefits.items.sections.title"),
        description: t("pages.plusBenefits.items.sections.description"),
        to: "/editor/sections",
        inUse: true,
      },
      {
        key: "analytics",
        title: t("pages.plusBenefits.items.analytics.title"),
        description: t("pages.plusBenefits.items.analytics.description"),
        to: "/analytics",
        inUse: true,
      },
      {
        key: "chat",
        title: t("pages.plusBenefits.items.chat.title"),
        description: t("pages.plusBenefits.items.chat.description"),
        to: "/help/chat",
        inUse: true,
      },
    ];

    const tracked: TrackedPlusBenefit[] = [
      ...always,
      {
        key: "brand",
        title: t("pages.plusBenefits.items.brand.title"),
        description: t("pages.plusBenefits.items.brand.description"),
        to: "/settings",
        inUse: !!site?.hideSignature,
      },
      {
        key: "pages",
        title: t("pages.plusBenefits.items.pages.title"),
        description: t("pages.plusBenefits.items.pages.description"),
        to: "/editor/pages",
        inUse: getPages(site).length > 0,
      },
      {
        key: "agenda",
        title: t("pages.plusBenefits.items.agenda.title"),
        description: t("pages.plusBenefits.items.agenda.description"),
        to: "/agenda",
        inUse: siteHasAgendaPage(site),
      },
      {
        key: "style",
        title: t("pages.plusBenefits.items.style.title"),
        description: t("pages.plusBenefits.items.style.description"),
        to: "/editor/style",
        // the brand is its own benefit (above): the look's other PLUS items
        inUse: !!site?.theme && siteUsesPlusStyle({ ...site, hideSignature: false }),
      },
      {
        key: "seo",
        title: t("pages.plusBenefits.items.seo.title"),
        description: t("pages.plusBenefits.items.seo.description"),
        to: "/seo",
        // the SEO screen keeps no `seo` while nothing is set (it follows the Keepo's own title and texts)
        inUse: !!site?.seo,
      },
      {
        key: "password",
        title: t("pages.plusBenefits.items.password.title"),
        description: t("pages.plusBenefits.items.password.description"),
        to: "/settings/password",
        inUse: !!appState.editingSitePasswordProtected,
      },
      ...sections,
      ...trackers,
    ];

    const included: PlusBenefit[] = [
      {
        key: "customDomain",
        title: t("pages.settings.components.advancedConfigs.customDomain.title"),
        description: t("pages.settings.components.advancedConfigs.customDomain.description"),
        to: "/settings/custom-domain",
      },
      {
        // in every text field ("Sugerir com IA"): nothing to tell whether it's used
        key: "ai",
        title: t("pages.plusBenefits.items.ai.title"),
        description: t("pages.plusBenefits.items.ai.description"),
        to: "/editor/sections",
      },
    ];

    return {
      tracked,
      included,
      used: tracked.filter(benefit => benefit.inUse).length,
      total: tracked.length,
    };
  }, [appState.editingSiteTrackingIntegrations, appState.editingSitePasswordProtected, site, t]);
};
